import React, { useContext, useEffect, useState } from 'react';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import * as _ from 'lodash';

import './ExaminedHistory.scss';
import moment from 'moment';
import SurfaceExaminedHistory from 'src/models/tract_detail/SurfaceExaminedHistory';
import { useForm } from 'react-hook-form';
import SurfaceExaminedHistoryChanges from 'src/models/tract_detail/SurfaceExaminedHistoryChanges';
import { FormRow } from 'src/components/controls/forms/reactHookForm';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import { useFetch } from 'use-http';
import { IAppContextState } from 'src/reducers/AppContext';
import { AppContext } from 'src/providers/AppContextProvider';

export interface IExaminedHistoryProps {
    dialogName: string;
    currentExaminedHistory: SurfaceExaminedHistory[];
    hideDialog: () => void;
    getData: () => void;
}

const SurfaceExaminedHistoryDialog = (props: IExaminedHistoryProps) => {
    const examinationTypes = [
        {value: 'Lease Check', label: 'Lease Check'},
        {value: 'Full Ownership', label: 'Full Ownership'},
        {value: 'Runsheet', label: 'Runsheet'},
        {value: 'Title Opinion', label: 'Title Opinion'},
        {value: 'Third Party Ownership', label: 'Third Party Ownership'},
        {value: 'Tax Records', label: 'Tax Records'},
    ];

    const recordTypeItems = [
        { value: 1, label: 'Book/Volume' },
        { value: 2, label: 'Instrument #' },
    ];

    const appContext = useContext<IAppContextState>(AppContext);

    const { reset, register, errors, handleSubmit, getValues } = useForm();
    const { post, response } = useFetch();

    const [surfaceExaminedHistoryList, setSurfaceExaminedHistoryList] = useState<SurfaceExaminedHistory[]>([]);
    const [deleteIds, setDeleteIds] = useState<number[]>([]);
    const [recordTypes, setRecordTypes] = useState<number[]>([]);

    useEffect(() => {
        resetExaminedHistory();
    }, [props.currentExaminedHistory]);

    useEffect(() => {
        let currentRecords: number[] = [];
        surfaceExaminedHistoryList.forEach(sel => {
            currentRecords.push(sel.recordTypeId ? parseInt(sel.recordTypeId + '', 10) : 0);
        });

        reset({examinedHistories: surfaceExaminedHistoryList});
        setRecordTypes(currentRecords);
    }, [surfaceExaminedHistoryList]);

    useEffect(() => {
        displayExaminedHistoryList();
    }, [recordTypes]);

    // const stopPropagate = (callback: () => void) => {
    //     return (e: {stopPropagation: () => void }) => {
    //         e.stopPropagation();
    //         callback();
    //     };
    // };

    const resetExaminedHistory = () => {
        if (props.currentExaminedHistory?.length > 0) {
            setSurfaceExaminedHistoryList([...props.currentExaminedHistory]);
        } else {
            setSurfaceExaminedHistoryList([]);
        }
    };

    const addNewExaminedHistory = () => {
        let ref = getValues();
        let histories = ref.examinedHistories ? ref.examinedHistories : [];
        let currentList = [...histories];
        let currentRecords = [...recordTypes];

        let newHistory: SurfaceExaminedHistory = {
            id: 0,
            surfaceTractId: 0,
            examinedByLandman: '',
            takeOffDate: null,
            ownershipType: '',
            examinedDate: null,
            recordTypeId: null,
            book: '',
            page: '',
            instrumentNumber: ''
        };

        currentList.push(newHistory);
        currentRecords.push(0);

        setSurfaceExaminedHistoryList(currentList);
        setRecordTypes(currentRecords);
    };

    const updateRecordType = (index: number) => {
        let currentRecords = [...recordTypes];
        let ref = getValues();

        currentRecords[index] = parseInt(ref.examinedHistories[index].recordTypeId, 10);

        setRecordTypes(currentRecords);
    };

    const onSubmit = async (data: { examinedHistories: SurfaceExaminedHistory[] }) => {
        let changes: SurfaceExaminedHistoryChanges = data.examinedHistories ? formatExaminedHistory(data.examinedHistories) : new SurfaceExaminedHistoryChanges();
        changes.deleteIds = deleteIds;

        await post(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/fields/${appContext.sectionNumber}/tracts/surface-tracts/${appContext.surfaceTractNumber}/surface-examined-histories`, changes);
        
        if (response.ok) {
            props.getData();
            onCloseDialog();
        } else {
            console.log('Unable to update Title Examination History');
        }

    };
    
    const formatExaminedHistory = (data: SurfaceExaminedHistory[]): SurfaceExaminedHistoryChanges => {
        let examinedHistoryChanges = new SurfaceExaminedHistoryChanges();
        
        data.forEach((history: SurfaceExaminedHistory, index: number) => {
            history.id = surfaceExaminedHistoryList.length > index ? surfaceExaminedHistoryList[index].id : 0;
            history.recordTypeId = history.recordTypeId ? parseInt(history.recordTypeId + '', 10) : null;
            
            history.examinedByLandman = history.examinedByLandman ? history.examinedByLandman : '';
            history.ownershipType = history.ownershipType ? history.ownershipType : '';
            history.book = history.book ? history.book : '';
            history.page = history.page ? history.page : '';
            history.instrumentNumber = history.instrumentNumber ? history.instrumentNumber : '';

            history.takeOffDate = history.takeOffDate
            ? moment(history.takeOffDate).toDate()
                : null;
                
            history.examinedDate = history.examinedDate
                ? moment(history.examinedDate).toDate()
                : null;

            if (history.id === 0) {
                examinedHistoryChanges.adds.push(history);
            } else {
                examinedHistoryChanges.updates.push(history);
            }
        });

        return examinedHistoryChanges;
    };

    const onRemove = (index: number) => {
        let newDeletes = [...deleteIds];
        let currentList = [...surfaceExaminedHistoryList];
        if (currentList[index]?.id > 0) {
            newDeletes.push(currentList[index].id);
            setDeleteIds(newDeletes);
        }

        currentList.splice(index, 1);

        setSurfaceExaminedHistoryList(currentList);
    };

    const onCloseDialog = () => {
        props.hideDialog();
        setDeleteIds([]);
        resetExaminedHistory();
    };

    const displayExaminedHistoryList = () => {
        return (
            <form 
                onSubmit={e => { 
                    e.stopPropagation();
                    return handleSubmit(onSubmit)(e);
                }}
            >
                <div style={{ minHeight: '40rem', maxHeight: '50rem', overflow: 'auto' }}>
                    {surfaceExaminedHistoryList?.map((field, index) => {
                        const recordTypeId = recordTypes[index];

                        return (
                            <div key={index} style={{ minWidth: '130rem', paddingBottom: '1rem', borderBottom: '1px solid #ccc' }}>
                                <FormRow>
                                    <RHFInputBox
                                        name={`examinedHistories[${index}].examinedDate`}
                                        label="Report Date"
                                        className="examined-history-field"
                                        type="date"
                                        isRequired={true}
                                        register={register}
                                        errors={errors}
                                    />
                                    <RHFInputBox
                                        name={`examinedHistories[${index}].examinedByLandman`}
                                        label="Examined By"
                                        className="lh-fifth-width examined-history-field"
                                        isRequired={true}
                                        register={register}
                                        errors={errors}
                                    />
                                    <RHFInputBox
                                        name={`examinedHistories[${index}].takeOffDate`}
                                        label="Certification Date"
                                        className="examined-history-field"
                                        type="date"
                                        register={register}
                                        errors={errors}
                                    />
                                    <RHFSelect
                                        name={`examinedHistories[${index}].ownershipType`}
                                        label="Examination Type"
                                        style={{ minWidth: '22rem' }}
                                        options={examinationTypes}
                                        rules={{ required: 'This field is required' }}
                                        register={register}
                                        errors={errors}
                                    />
                                    <RHFSelect
                                        name={`examinedHistories[${index}].recordTypeId`}
                                        label="Record Type"
                                        options={recordTypeItems}
                                        style={{ minWidth: '16rem' }}
                                        onChange={() => updateRecordType(index)}
                                        rules={{ required: 'This field is required' }}
                                        register={register}
                                        errors={errors}
                                    />
                                    {recordTypeId === 0 && (
                                        <>
                                            <div className="lh-fifth-width" />
                                            <div className="lh-fifth-width" />
                                        </>
                                    )}
                                    {recordTypeId === 1 && (
                                        <>
                                            <RHFInputBox
                                                name={`examinedHistories[${index}].book`}
                                                label="Book"
                                                isRequired={recordTypeId === 1}
                                                disabled={recordTypeId !== 1}
                                                className="lh-fifth-width"
                                                register={register}
                                                errors={errors}        
                                            />

                                            <RHFInputBox
                                                name={`examinedHistories[${index}].page`}
                                                label="Page"
                                                isRequired={recordTypeId === 1}
                                                disabled={recordTypeId !== 1}
                                                className="lh-fifth-width"
                                                register={register}
                                                errors={errors}        
                                            />
                                        </>
                                    )}
                                    {recordTypeId === 2 && (
                                        <>
                                            <RHFInputBox
                                                name={`examinedHistories[${index}].instrumentNumber`}
                                                label="Instrument #"
                                                isRequired={recordTypeId === 2}
                                                disabled={recordTypeId !== 2}
                                                className="lh-fifth-width"
                                                register={register}
                                                errors={errors}        
                                            />
                                            <div className="lh-fifth-width" />
                                        </>
                                    )}
                                    <div
                                        className="delete-examined-history"
                                        onClick={() => onRemove(index)}
                                        style={{ padding: '1.4rem 0' }}
                                    >
                                        <i className="fas fa-trash" />
                                    </div>
                                </FormRow>
                            </div>
                        );
                    })}

                </div>
                <div style={{ marginTop: '5px' }}>
                    <Button
                        onClick={() => addNewExaminedHistory()}
                        text="Add Entry"
                        intent="success"
                    />
                </div>

                <hr />

                <div style={{ display: 'flex', textAlign: 'right' }}>
                    <FormRow>
                        <Button intent="primary" type="submit" text="Save" />
                        <Button intent="warning" onClick={() => onCloseDialog()} text="Cancel" />
                    </FormRow>
                </div>
            </form>
        );
    };

    return (
        <Dialog dialogName={props.dialogName} title="Title Examination History" onCloseDialog={() => onCloseDialog()}>
            <div>
                {displayExaminedHistoryList()}

                <div>
                    <Button onClick={addNewExaminedHistory} text="Add Entry" />
                </div>
            </div>
        </Dialog>
    );
};

export default SurfaceExaminedHistoryDialog;



// WEBPACK FOOTER //
// ./src/components/project/sections/TractComponents/SurfaceExaminedHistoryDialog.tsx